import { Lightning } from '@lightningjs/sdk';
import _memoize from 'lodash/memoize';
import _mapKeys from 'lodash/mapKeys';
import basic from './basic';
import controls from './controls';
import special from './special';

/**
 * @typedef {string} IconCollection
 */

/**
 * @enum {IconCollection}
 */
export const ICON_COLLECTION = {
  BASIC: 'basic',
  CONTROLS: 'controls',
  SPECIAL: 'special',
};

const objectKeysToLowerCase = obj => _mapKeys(obj, (_, k) => k.toLowerCase());

const mapping = {
  [ICON_COLLECTION.BASIC]: objectKeysToLowerCase(basic),
  [ICON_COLLECTION.CONTROLS]: objectKeysToLowerCase(controls),
  [ICON_COLLECTION.SPECIAL]: objectKeysToLowerCase(special),
};

/**
 * Provides base path basing on host name. Memoization provides lazy execution and guarantees single-time evaluation.
 */
export const getBasePath = _memoize(
  () => window.location.origin || `${window.location.protocol}//${window.location.host || window.location.hostname}`,
);

export const getCollectionById = collectionId => mapping[collectionId] || basic;

/**
 * Provides a local URL to an icon asset
 *
 * @param {string} name
 * @param {IconCollection} collection
 * @returns {string}
 */
export const iconByName = (name, collection = ICON_COLLECTION.BASIC) => getCollectionById(collection)[name];

/**
 * Renders SVG to bitmap scaling it up or down to sizes of view box with aspect ratio kept as specified in SVG attributes
 * NOTE: this is mostly copied from Lightning code, with aspect ration modification added on top
 *
 * @param {Function} callback image drawing callback
 * @param {lightning.Stage} stage Lightning stage
 * @param {string} url absolute URL of SVG
 * @param {number} width view box width
 * @param {number} height view box height
 */
export const createSvg = (callback, stage, url, width, height, scale = 1) => {
  const canvas = stage.platform.getDrawingCanvas();
  const context = canvas.getContext('2d');
  context.imageSmoothingEnabled = scale === 1;

  const image = new Image();
  image.onload = () => {
    const imageWidth = image.width;
    const imageHeight = image.height;
    const viewBoxWidth = width || 0;
    const viewBoxHeight = height;
    const horizontalRatio = viewBoxWidth ? imageWidth / viewBoxWidth : 0;
    const verticalRatio = viewBoxHeight ? imageHeight / viewBoxHeight : 0;
    const scaleRatio = horizontalRatio > verticalRatio ? viewBoxWidth / imageWidth : viewBoxHeight / imageHeight;

    canvas.width = Math.round(imageWidth * scaleRatio);
    canvas.height = Math.round(imageHeight * scaleRatio);

    canvas.width *= scale;
    canvas.height *= scale;

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    callback(null, canvas);
  };
  image.onError = error => {
    callback(error);
  };

  // On the PS4 platform setting the `crossOrigin` attribute on images can cause CORS failures.
  // TODO: use platform replacement
  if (!Lightning.Utils.isPS4) {
    image.crossOrigin = 'Anonymous';
  }

  image.src = url;
};

/**
 * Generates Lightning texture from SVG
 *
 * @param {string} url string
 * @param {number} w number
 * @param {number} h number
 * @returns {Object} Lightning texture
 */
export const getSvgTexture = (url, w, h, scale = 1) => {
  const factory = (callback, stage) => {
    if (Lightning.Utils.isSpark) {
      stage.platform.createSvg(callback, stage, url, w, h);
    } else {
      createSvg(callback, stage, url, w, h, scale);
    }
  };
  const id = `svg${[w, h, scale, url].join(',')}`;

  return Lightning.Tools.getCanvasTexture(factory, id);
};

export * as basic from './basic';

export * as controls from './controls';

export * as special from './special';
